import { randomInt, random, clamp } from '@CS/utils/Number';
import { DELTA_FRAME, RAD } from '@CS/utils/Number';
import deepFreeze from '@CS/utils/deepFreeze';
import Point from './Point';

const COLORS = deepFreeze(
  [...Array(5)].map(() => [...Array(3)].map(() => randomInt(0, 255)))
);

export default abstract class Particle {
  protected points!: [Point, Point, Point, Point];

  protected readonly useFlip: boolean = true;
  protected readonly position = new Point();

  protected particleHeight: number = 0.0;
  protected particleWidth: number = 0.0;

  protected height = window.innerHeight;
  protected width = window.innerWidth;

  private readonly useEasing = false;
  public static DropOnce: boolean;

  protected running = false;
  protected duration = 0.0;

  protected opacity = 1.0;
  protected rgb!: string;

  protected radius = 0.0;
  protected fade = 1.0;

  private time = 0.0;
  private flip = 0.0;

  public constructor(protected readonly context: CanvasRenderingContext2D) {
    this.setColor();
    this.setPosition();
    this.setDuration();
  }

  public start(): void {
    this.running = true;

    this.points = [
      new Point(random(0.0, this.width), -random(0.0, this.height)),
      new Point(random(0.0, this.width), random(0.0, this.height)),
      new Point(random(0.0, this.width), random(0.0, this.height)),
      new Point(random(0.0, this.width), this.height - this.position.y)
    ];
  }

  protected update(): void {
    const time = this.getNormalizedTime();
    this.time = Math.min(this.time + DELTA_FRAME, this.duration);

    if (this.fade) this.opacity = clamp(this.fade - time);
    if (this.time === this.duration) return this.reset();

    const point = this.position;
    const position = this.cubicBezier(time, ...this.points);

    this.flip = Math.sin(Math.PI * time * 10.0);
    this.radius = position.angleTo(point);

    position.copyTo(point);
  }

  public draw(path = false): void {
    this.update();

    this.context.save();
    path && this.context.beginPath();
    this.context.translate(this.position.x, this.position.y);

    this.context.rotate(this.radius + RAD);
    this.useFlip && this.context.scale(1.0, this.flip);
  }

  protected reset(): void {
    this.radius = this.time = this.flip = 0.0;

    this.setPosition();
    this.setDuration();

    this.setColor();
    this.start();
  }

  public stop(): void {
    this.running = false;
    this.fade = 0.0;
  }

  private cubicBezier(
    t: number,
    p0: Point,
    p1: Point,
    p2: Point,
    p3: Point
  ): Point {
    const l = 1.0 - t;

    return new Point(
      l * l * l * p0.x +
        3 * l * l * t * p1.x +
        3 * l * t * t * p2.x +
        t * t * t * p3.x,

      l * l * l * p0.y +
        3 * l * l * t * p1.y +
        3 * l * t * t * p2.y +
        t * t * t * p3.y
    );
  }

  private setColor(): void {
    const [r, g, b] = COLORS[randomInt(0, COLORS.length - 1)];
    this.rgb = `${r}, ${g}, ${b}`;
  }

  protected setPosition(): void {
    this.position.x = random(0.0, this.width);
    this.position.y = -Math.max(this.particleWidth, this.particleHeight);
  }

  protected setDuration(): void {
    this.duration = random(5.0, 10.0);
  }

  public setSize(width: number, height: number): void {
    this.height = height;
    this.width = width;
  }

  private getNormalizedTime(time = this.time / this.duration): number {
    return this.useEasing ? --time * time * time + 1.0 : time;
  }
}
